import {assigneesSectionAssignees} from '@github-ui/issue-metadata/AssigneesSection'
import type {AssigneesSectionAssignees$key} from '@github-ui/issue-metadata/AssigneesSectionAssignees.graphql'
import {LabelsSectionAssignedLabelsFragment} from '@github-ui/issue-metadata/LabelsSection'
import type {LabelsSectionAssignedLabels$key} from '@github-ui/issue-metadata/LabelsSectionAssignedLabels.graphql'
import {milestonesSectionMilestone} from '@github-ui/issue-metadata/MilestonesSection'
import type {MilestonesSectionMilestone$key} from '@github-ui/issue-metadata/MilestonesSectionMilestone.graphql'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {LabelsList} from '@github-ui/labels-list'
import {type ReactElement, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueMetadata$key} from './__generated__/IssueMetadata.graphql'
import {AssigneesMetadata} from './AssigneesMetadata'
import styles from './IssueMetadata.module.css'
import {MilestoneMetadata} from './MilestoneMetadata'

/*
  This fragment fetches metadata for the issue header, including assignees, labels, and milestones.
  It is primarily used for mobile views but is not conditionally accessed due to similar fragments in the IssueSidebar within the IssueViewer.
  Relay optimizes data fetching by retrieving the data only once in such cases.
  If this component is used outside the IssueViewer, this fragment should be conditionally included for mobile screens only.
*/
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueMetadataFragment = graphql`
  fragment IssueMetadata on Issue {
    ...LabelsSectionAssignedLabels
    ...AssigneesSectionAssignees
    ...MilestonesSectionMilestone
  }
`

export const IssueMetadata = ({metadataKey}: {metadataKey: IssueMetadata$key}) => {
  const issueMetadata = useFragment(IssueMetadataFragment, metadataKey)
  const labelDataRaw = useFragment<LabelsSectionAssignedLabels$key>(LabelsSectionAssignedLabelsFragment, issueMetadata)
  const assigneesDataRaw = useFragment<AssigneesSectionAssignees$key>(assigneesSectionAssignees, issueMetadata)
  const milestoneDataRaw = useFragment<MilestonesSectionMilestone$key>(milestonesSectionMilestone, issueMetadata)

  const assigneesData = useMemo(() => {
    return ((assigneesDataRaw?.assignedActors?.nodes || []).flatMap(assignee => {
      return assignee ? [assignee] : []
    }) ?? []) as AssigneePickerAssignee[]
  }, [assigneesDataRaw?.assignedActors?.nodes])

  const labelsData = useMemo(() => {
    return (
      (labelDataRaw?.labels?.edges || []).flatMap(label => {
        return label?.node ? [label?.node] : []
      }) ?? []
    )
  }, [labelDataRaw?.labels?.edges])

  const milestoneData = milestoneDataRaw?.milestone ? milestoneDataRaw?.milestone : null

  // return early if no data is available
  if (!assigneesData.length && !labelsData.length && !milestoneData) {
    return null
  }

  return (
    <div className={styles.issueMetadata}>
      {assigneesData?.length > 0 && (
        <Metadata title="Assignees">
          <AssigneesMetadata assignees={assigneesData} />
        </Metadata>
      )}
      {labelsData?.length > 0 && (
        <Metadata title="Labels">
          <LabelsList labels={labelsData} />
        </Metadata>
      )}
      {milestoneData && (
        <Metadata title="Milestone">
          <MilestoneMetadata milestone={milestoneData} />
        </Metadata>
      )}
    </div>
  )
}

const Metadata = ({title, children}: {title: string; children: ReactElement}) => (
  <div className={styles.metadata}>
    <div className={styles.metadataTitle}>{title}</div>
    <div className={styles.metadataValue}>{children}</div>
  </div>
)
